/** Point drain for the wash-down corridor; its physical fall retains a dark, readable void below the grate. */
import * as THREE from 'three';
import { surface } from '../materials';
import { seatOnOrigin } from '../origin';
import type { AssetDefinition } from '../types';

function build(): THREE.Group {
  const group = new THREE.Group();
  group.name = 'floor-gully';
  const rim = surface('metal', 0x737d7e);
  const grate = surface('metal', 0x9aa3a2);
  const voidMaterial = surface('metal', 0x202828);

  for (const [x, z, width, length, name] of [
    [0, -0.21, 0.45, 0.03, 'frame-back'], [0, 0.21, 0.45, 0.03, 'frame-front'],
    [-0.21, 0, 0.03, 0.39, 'frame-left'], [0.21, 0, 0.03, 0.39, 'frame-right'],
  ] as const) {
    const edge = new THREE.Mesh(new THREE.BoxGeometry(width, 0.06, length), rim);
    edge.name = name;
    edge.position.set(x, 0.03, z);
    edge.castShadow = true;
    group.add(edge);
  }

  // This follows drain-channel's answer to an in-floor asset: the apparent drop
  // remains just above the shared ground datum, so placement never asks callers
  // to compensate for geometry below y=0.
  const voidFloor = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.006, 0.36), voidMaterial);
  voidFloor.name = 'recessed-dark-void';
  voidFloor.position.set(0, 0.009, 0);
  voidFloor.receiveShadow = true;
  group.add(voidFloor);

  for (let index = 0; index < 8; index += 1) {
    const bar = new THREE.Mesh(new THREE.BoxGeometry(0.36, 0.012, 0.02), grate);
    bar.name = `grate-bar-${index + 1}`;
    bar.position.set(0, 0.054, -0.14 + index * 0.04);
    bar.castShadow = true;
    group.add(bar);
  }

  // Authored from a convenient face outward, then seated on the contract's
  // origin. See `seatOnOrigin` for why five assets in this batch needed it.
  seatOnOrigin(group);

  return group;
}

export const floorGullyAsset: AssetDefinition = {
  id: 'floor-gully',
  label: 'Square floor gully',
  description: '0.45 m square recessed point drain with a steel rim, eight grate bars, and seven dark slots.',
  footprint: { width: 0.45, height: 0.06, length: 0.45 },
  create: build,
};
